import { useAdminApi } from '@hooks/useAdminApi';
import { useApi } from '@hooks/useApi';
import { Box, Checkbox, Stack, Typography } from '@mui/material';
import { Product } from '@protos/product';
import { TradeLimit, User } from '@protos/user';
import { toastifyService } from '@services/ToastifyService';
import { IconComponent } from '@shared/components/IconComponent';
import { SearchGroupedProducts } from '@shared/components/SearchGroupedProducts';
import { TextField } from '@shared/components/TextField';
import { StyledInputLabel } from '@shared/components/TextInput';
import { useUpdateUserSettings } from '@shared/contexts/UserContextProvider';
import { useEffect, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { getCurrentUser } from '../utils';
import { BaseEditDialog } from './BaseEditDialog';
import { StyledIconButton } from './StyledIconButton';
import { OtcExchange, OtcOrderType, OtcOrderTypeDisplayTextMap } from '@protos/v2/otcQuote';
import { checkIsLimitOrderTypesAllowed } from '@utils/permissions';

interface EditTradeLimitsDialogProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: (user: User) => void;
  isPrivateAllowed: boolean;
}

const NumberRegex = new RegExp('^[0-9]*$');
const exchangeOptions: OtcExchange[] = [OtcExchange.ice, OtcExchange.cme];
const orderTypeOptions: OtcOrderType[] = [OtcOrderType.fill_or_kill, OtcOrderType.limit];
const icon = <IconComponent name="ri-checkbox-blank-line" />;
const checkedIcon = <IconComponent name="ri-checkbox-line" />;

export const EditTradeLimitsDialog: React.FC<EditTradeLimitsDialogProps> = ({ user, isOpen, onClose, onUserUpdated, isPrivateAllowed = false }) => {
  const { apiClient } = useAdminApi();
  const { apiClient: userApiClient } = useApi();
  const updateUserSettings = useUpdateUserSettings();
  const [tradeLimits, setTradeLimits] = useState<TradeLimit[]>([...user.trade_limits]);
  const currentUser = getCurrentUser(user);

  const products = useAsync(async () => {
    const products = await apiClient?.productsLoader({ tradable: true }).loadAllData();
    return {
      allSymbols: products || [],
      privateSymbols: products?.filter(product => product.private) || [],
    };
  }, [apiClient]);

  const defaultTradeLimits = useAsync(async () => {
    const response = await apiClient?.getDefaultProductTradeLimits();
    return response || [];
  }, [apiClient]);

  const nonPrivateProducts = useMemo(() => {
    if (products.loading || !products.value) return [];
    return products.value.allSymbols.filter(p => !products.value.privateSymbols.includes(p));
  }, [products]);

  const allProductGroups = useMemo(() => {
    const groups: { [key: string]: Product[] } = {};
    if (products.loading || !products.value) return groups;
    const prodList = isPrivateAllowed ? products.value.allSymbols : nonPrivateProducts;

    prodList.forEach(product => {
      if (!groups[product.product_group]) {
        groups[product.product_group] = [];
      }
      groups[product.product_group].push(product);
    });
    return groups;
  }, [products, isPrivateAllowed]);

  const selectedProducts = useMemo(() => {
    if (products.loading || !products.value) return [];
    const prodList = isPrivateAllowed ? products.value.allSymbols : nonPrivateProducts;

    return prodList.filter(p => tradeLimits.map(t => t.symbol).includes(p.symbol));
  }, [products, tradeLimits, isPrivateAllowed]);

  const onSave = async () => {
    if (apiClient && user && userApiClient && updateUserSettings) {
      try {
        const updatedUser = await apiClient.updateUserLimits(user.id, { ...currentUser, trade_limits: tradeLimits });
        setTradeLimits(updatedUser.trade_limits);
        onUserUpdated(updatedUser);

        const currentSettings = await userApiClient.getUserSettings();
        if (currentSettings) {
          const currentUserSettingsLimits = currentSettings.ocl_trading?.trade_limits || [];

          const settingsLimitsMap = new Map(currentUserSettingsLimits.map(limit => [limit.symbol, limit]));

          const mergedLimits = tradeLimits.map(userLimit => {
            const existingSettingsLimit = settingsLimitsMap.get(userLimit.symbol);

            if (existingSettingsLimit) {
              return {
                ...existingSettingsLimit,
                limit: userLimit.limit,
              };
            } else {
              return userLimit;
            }
          });

          const userSymbols = new Set(tradeLimits.map(limit => limit.symbol));
          const filteredMergedLimits = mergedLimits.filter(limit => userSymbols.has(limit.symbol));

          await updateUserSettings({
            ...currentSettings,
            ocl_trading: {
              ...currentSettings.ocl_trading,
              trade_limits: filteredMergedLimits,
            },
          });
        }
      } catch (error) {
        console.error('Failed to update trade limits:', error);
        toastifyService.showError('Failed to update trade limits');
      }
    }
  };

  const onTradeLimitAdd = (product: Product) => {
    const defaultLimit = defaultTradeLimits.value?.find(limit => limit.symbol === product.symbol)?.limit || 0;
    setTradeLimits([
      ...tradeLimits,
      { symbol: product.symbol, limit: defaultLimit, exchange: exchangeOptions, order_types: [OtcOrderType.fill_or_kill] },
    ]);
  };

  const onTradeLimitRemove = (tradeId: number) => {
    const updatedTrades = [...tradeLimits];
    updatedTrades.splice(tradeId, 1);
    setTradeLimits(updatedTrades);
  };

  const onCheckboxChange = (product: Product) => {
    const ladderId = tradeLimits.findIndex(limit => limit.symbol === product.symbol);
    if (ladderId === -1) onTradeLimitAdd(product);
    else onTradeLimitRemove(ladderId);
  };

  const onTradeLimitUpdate = (tradeId: number, updatedValue: TradeLimit) => {
    const updatedTrades = [...tradeLimits];
    updatedTrades.splice(tradeId, 1, updatedValue);
    setTradeLimits(updatedTrades);
  };

  const onExchangeCheckboxClick = (tradeId: number, value: OtcExchange, tradeLimit: TradeLimit) => {
    let updatedTradeExchanges = [...tradeLimits[tradeId].exchange];
    if (updatedTradeExchanges.includes(value)) {
      updatedTradeExchanges = updatedTradeExchanges.filter(ex => ex !== value);
    } else {
      updatedTradeExchanges.push(value);
    }
    onTradeLimitUpdate(tradeId, { ...tradeLimit, exchange: updatedTradeExchanges });
  };

  const onOrderTypeCheckboxClick = (tradeId: number, value: OtcOrderType, tradeLimit: TradeLimit) => {
    let updatedTradeOrderTypes = [...tradeLimits[tradeId].order_types];
    if (updatedTradeOrderTypes.includes(value)) {
      updatedTradeOrderTypes = updatedTradeOrderTypes.filter(ot => ot !== value);
    } else {
      updatedTradeOrderTypes.push(value);
    }
    onTradeLimitUpdate(tradeId, { ...tradeLimit, order_types: updatedTradeOrderTypes });
  };

  useEffect(() => {
    setTradeLimits(user.trade_limits);
  }, [user, isOpen]);

  if (products.loading || !products.value) return null;

  return (
    <BaseEditDialog
      title="Edit Trade Limits"
      isOpen={isOpen}
      onClose={onClose}
      clearErrorOn={[tradeLimits]}
      defaultErrorMessage="There was an error updating the trade limits"
      onSave={onSave}
    >
      <Box display="flex" marginRight={2} gap={1}>
        <Stack direction="column" alignItems="flex-start" style={{ height: '80vh' }}>
          <div style={{ flexGrow: 1, overflowY: 'auto', width: '100%' }}>
            {tradeLimits.map((tradeLimit, id) => (
              <Stack key={`trade_limit_${tradeLimit.symbol}`} direction="row" alignItems="center">
                <Typography variant="h6" minWidth="120px">
                  {tradeLimit.symbol}
                </Typography>
                <div style={{ margin: '0 15px' }}>
                  <TextField
                    label="Limit"
                    value={tradeLimit.limit.toString()}
                    error={tradeLimit.limit === 0}
                    sx={{ width: '100px' }}
                    onChange={(value: string) => {
                      if (NumberRegex.test(value)) onTradeLimitUpdate(id, { ...tradeLimit, limit: Number(value) });
                    }}
                  />
                </div>
                <div style={{ padding: '8px 10px 13px' }}>
                  <StyledInputLabel sx={{ justifySelf: 'center' }}>Exchanges:</StyledInputLabel>
                  <Box display="flex" flexDirection="row">
                    {exchangeOptions.map(exOption => (
                      <Box display="flex" alignItems="center" key={`checkbox-${exOption}-${id}`} style={{ marginRight: 8 }}>
                        <Checkbox
                          size="small"
                          icon={icon}
                          checkedIcon={checkedIcon}
                          checked={tradeLimit.exchange.includes(exOption)}
                          onChange={() => onExchangeCheckboxClick(id, exOption, tradeLimit)}
                        />
                        <Typography fontSize={11}>{exOption}</Typography>
                      </Box>
                    ))}
                  </Box>
                </div>
                {checkIsLimitOrderTypesAllowed(user) && (
                  <div style={{ padding: '8px 10px 13px' }}>
                    <StyledInputLabel sx={{ justifySelf: 'center' }}>Order Types:</StyledInputLabel>
                    <Box display="flex" flexDirection="row">
                      {orderTypeOptions.map(orderType => (
                        <Box display="flex" alignItems="center" key={`checkbox-${orderType}-${id}`} style={{ marginRight: 8 }}>
                          <Checkbox
                            size="small"
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={tradeLimit?.order_types?.includes(orderType)}
                            onChange={() => onOrderTypeCheckboxClick(id, orderType, tradeLimit)}
                          />
                          <Typography fontSize={11}>{OtcOrderTypeDisplayTextMap.get(orderType) || ''}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </div>
                )}
                <StyledIconButton
                  onClick={() => onTradeLimitRemove(id)}
                  size={'small'}
                  style={{ marginLeft: 'auto', alignSelf: 'center', marginTop: '2%' }}
                >
                  <i className={'ri-delete-bin-line'} />
                </StyledIconButton>
              </Stack>
            ))}
          </div>

          <Stack marginTop={2} width="550px" style={{ flexGrow: 1 }}>
            <SearchGroupedProducts
              allProductGroups={allProductGroups}
              allSelectedProducts={selectedProducts}
              selectedUserProducts={selectedProducts}
              onProductClick={onCheckboxChange}
              getProductCount={() => selectedProducts.length}
              isSelectionDisabled={() => false}
              maxProductsLimit={products.value.allSymbols.length}
            />
          </Stack>
        </Stack>
      </Box>
    </BaseEditDialog>
  );
};
